import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import styles from './DashboardSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';

const MONTH_NAMES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const PIE_COLORS = ['#1e1140', '#e85a1b', '#7c3aed', '#059669', '#d97706'];
const AUDIENCE_CONFIG = {
  inscriptions: {
    label: 'Inscripciones',
    singular: 'inscripción',
    monthlyTitle: 'Inscripciones por mes',
    careersTitle: 'Carreras con más inscripciones',
    tableTitle: 'Alumnos inscritos',
  },
  prospects: {
    label: 'Prospectos',
    singular: 'prospecto',
    monthlyTitle: 'Prospectos por mes',
    careersTitle: 'Carreras con más prospectos',
    tableTitle: 'Prospectos registrados',
  },
};

function authHeaders() {
  const token = localStorage.getItem('odm_token');
  return { Authorization: `Bearer ${token}` };
}

function StatCard({ label, value, sub }) {
  return (
    <div className={styles.statCard}>
      <p className={styles.statLabel}>{label}</p>
      <p className={styles.statValue}>{value}</p>
      {sub && <p className={styles.statSub}>{sub}</p>}
    </div>
  );
}

export default function DashboardSection() {
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', career: '' });
  const [degrees, setDegrees] = useState([]);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [audience, setAudience] = useState('inscriptions');

  useEffect(() => {
    fetch(`${API}/degrees/list`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(({ data }) => setDegrees(data || []));
  }, []);

  const buildQuery = useCallback((selectedFilters, selectedAudience, extra = {}) => {
    const p = { ...selectedFilters, ...extra };
    const q = new URLSearchParams();
    if (p.dateFrom) q.set('dateFrom', p.dateFrom);
    if (p.dateTo) q.set('dateTo', p.dateTo);
    if (p.career) q.set('career', p.career);
    if (p.page) q.set('page', p.page);
    q.set('audience', selectedAudience);
    return q.toString();
  }, []);

  const fetchAll = useCallback((currentPage = 1, selectedAudience = audience, selectedFilters = filters) => {
    setLoading(true);
    const qs = buildQuery(selectedFilters, selectedAudience, { page: currentPage });
    Promise.all([
      fetch(`${API}/stats/inscriptions?${qs}`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API}/stats/students?${qs}&limit=10`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API}/stats/careers?${qs}`, { headers: authHeaders() }).then((r) => r.json()),
    ]).then(([inscData, studData, carData]) => {
      setStats(inscData.data || null);
      setStudents(studData.data || []);
      setPagination(studData.pagination || null);
      setCareers(carData.data || []);
    }).finally(() => setLoading(false));
  }, [audience, buildQuery, filters]);

  useEffect(() => { fetchAll(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAll(1);
  };

  const changePage = (newPage) => {
    setPage(newPage);
    fetchAll(newPage);
  };

  const changeAudience = (nextAudience) => {
    if (nextAudience === audience) return;
    setAudience(nextAudience);
    setPage(1);
    fetchAll(1, nextAudience);
  };

  const clearFilters = () => {
    const emptyFilters = { dateFrom: '', dateTo: '', career: '' };
    setFilters(emptyFilters);
    setPage(1);
    fetchAll(1, audience, emptyFilters);
  };

  const audienceConfig = AUDIENCE_CONFIG[audience];

  const byMonthData = (stats?.byMonth || []).map((m) => ({
    name: `${MONTH_NAMES[m.month]} ${m.year}`,
    registros: m.count,
  }));

  const byPlanData = (stats?.byPlan || []).map((p) => ({
    name: p.plan,
    value: p.count,
  }));

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Dashboard</h1>

      {/* ── Filters ── */}
      <form className={styles.filters} onSubmit={handleFilter}>
        <label className={styles.filterLabel}>
          Desde
          <input
            type="date"
            className={styles.filterInput}
            value={filters.dateFrom}
            onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))}
          />
        </label>
        <label className={styles.filterLabel}>
          Hasta
          <input
            type="date"
            className={styles.filterInput}
            value={filters.dateTo}
            onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))}
          />
        </label>
        <label className={styles.filterLabel}>
          Carrera
          <select
            className={styles.filterInput}
            value={filters.career}
            onChange={(e) => setFilters((p) => ({ ...p, career: e.target.value }))}
          >
            <option value="">Todas</option>
            {degrees.map((d) => (
              <option key={d._id} value={d._id}>{d.name}</option>
            ))}
          </select>
        </label>
        <button type="submit" className={styles.filterBtn}>Aplicar</button>
        <button
          type="button"
          className={styles.filterBtnSecondary}
          onClick={clearFilters}
        >
          Limpiar
        </button>
      </form>

      {loading && <p className={styles.loading}>Cargando datos...</p>}

      {/* ── Audience summary and selector ── */}
      {stats && (
        <div className={styles.audienceSummary}>
          <StatCard label="Inscripciones con pago" value={stats.summary?.inscriptions ?? 0} />
          <StatCard label="Prospectos sin pago" value={stats.summary?.prospects ?? 0} />
        </div>
      )}

      <div className={styles.audienceTabs} role="tablist" aria-label="Tipo de registro">
        {Object.entries(AUDIENCE_CONFIG).map(([key, config]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={audience === key}
            className={`${styles.audienceTab} ${audience === key ? styles.audienceTabActive : ''}`}
            onClick={() => changeAudience(key)}
          >
            {config.label}
            {stats?.summary && (
              <span className={styles.audienceCount}>{stats.summary[key] ?? 0}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Selected audience stats ── */}
      {stats && (
        <div className={styles.statCards}>
          <StatCard label={`Total ${audienceConfig.label.toLowerCase()}`} value={stats.total} />
          {stats.byPlan.map((p) => (
            <StatCard
              key={p.plan}
              label={p.plan}
              value={p.count}
              sub={`${p.count === 1 ? audienceConfig.singular : audienceConfig.label.toLowerCase()}`}
            />
          ))}
        </div>
      )}

      {/* ── Charts row ── */}
      <div className={styles.chartsRow}>
        {/* Bar chart: by month */}
        <div className={styles.chartCard}>
          <h2 className={styles.chartTitle}>{audienceConfig.monthlyTitle}</h2>
          {byMonthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byMonthData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar
                  dataKey="registros"
                  name={audienceConfig.label}
                  fill="#1e1140"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.noData}>Sin datos</p>
          )}
        </div>

        {/* Pie chart: by plan */}
        <div className={styles.chartCard}>
          <h2 className={styles.chartTitle}>Distribución por plan</h2>
          {byPlanData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={byPlanData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {byPlanData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.noData}>Sin datos</p>
          )}
        </div>

        {/* Bar chart: top careers */}
        <div className={styles.chartCard}>
          <h2 className={styles.chartTitle}>{audienceConfig.careersTitle}</h2>
          {careers.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={careers.slice(0, 6).map((c) => ({ name: c.careerName?.split(' ').slice(-2).join(' '), count: c.count }))}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
              >
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#e85a1b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.noData}>Sin datos</p>
          )}
        </div>
      </div>

      {/* ── Students table ── */}
      <div className={styles.tableCard}>
        <h2 className={styles.chartTitle}>{audienceConfig.tableTitle}</h2>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Carrera</th>
                <th>Plan</th>
                <th>Email verificado</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s._id}>
                  <td>{s.firstName} {s.lastName} {s.motherLastName || ''}</td>
                  <td>{s.email}</td>
                  <td>{s.phone}</td>
                  <td className={styles.careerCell}>{s.careerName}</td>
                  <td>{s.planLabel}</td>
                  <td className={s.emailVerified ? styles.verified : styles.unverified}>
                    {s.emailVerified ? 'Sí' : 'No'}
                  </td>
                  <td>{new Date(s.createdAt).toLocaleDateString('es-MX')}</td>
                </tr>
              ))}
              {students.length === 0 && !loading && (
                <tr><td colSpan={7} className={styles.noData}>Sin registros.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className={styles.pagination}>
            <button className={styles.pageBtn} disabled={page === 1} onClick={() => changePage(page - 1)}>‹</button>
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
                onClick={() => changePage(p)}
              >
                {p}
              </button>
            ))}
            <button className={styles.pageBtn} disabled={page === pagination.pages} onClick={() => changePage(page + 1)}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}
